// This source code was originally copy-pasted from `fflate`'s repository
// and then refactored according to my personal taste. Also added comments.
// https://github.com/101arrowz/fflate/blob/master/src/worker.ts
/**
 * Creates a worker in a web browser.
 *
 * Defines a `var postMessage = (data, [transferList]) => ...` function.
 * Requires a `var onMessage = (data) => ...` function to be defined.
 *
 * @param {string} javascriptCode
 * @param {function} getFromCache — Could be used to add caching. Has no arguments. Returns the cached value.
 * @param {function} setInCache — Could be used to add caching. Receives the value to cache as an argument. Doesn't return anything.
 * @param {function} onError — This function will be called every time when there was an error while processing an incoming message. It would be logical to call `worker.terminate()` inside this function.
 * @param {function} onOutput — This function will be called every time when done processing an incoming message.
 * @returns {Worker} — An object with methods: `ingest(data, transferList)`, `stop()`.
 */
export default function createWorkerInBrowser(javascriptCode, getFromCache, setInCache, onError, onOutput) {
    // Worker's code URL — "blob://..."
    let url = getFromCache();
    if (!url) {
        url = createWorkerCodeUrl(javascriptCode);
        setInCache(url);
    }
    // Create a worker.
    const worker = new Worker(url);
    // A "message" event fires in the main thread (i.e. here) whenever the worker
    // sends a message to the main thread. Because this code is the main thread,
    // here it sets up a message listener to receive results from the worker.
    // Upon receiving a result (or an error) from the worker, it calls the `callback`.
    worker.onmessage = (event) => {
        const data = event.data;
        const errorData = data ? data[ERROR_MESSAGE_PROPERTY_NAME] : undefined;
        if (errorData) {
            const error = new Error(errorData[0]);
            error.stack = errorData[1];
            let i = 2;
            while (i < errorData.length) {
                error[errorData[i][0]] = errorData[i][1];
                i++;
            }
            onError(error);
            // Terminate the worker thread to mimick Node.js's "unrecoverable" behavior.
            worker.terminate();
        }
        else {
            onOutput(data);
        }
    };
    return {
        // Calling `worker.terminate()` will kill the worker thread immediately.
        // Calling `worker.terminate()` multiple times is safe and will not throw any errors.
        stop: worker.terminate.bind(worker),
        ingest: worker.postMessage.bind(worker)
    };
}
function createWorkerCodeUrl(javascriptCode) {
    return URL.createObjectURL(new Blob([
        javascriptCode + ';' + JAVASCRIPT_CODE_ADDITIONAL
    ], { type: 'text/javascript' }));
}
const ERROR_MESSAGE_PROPERTY_NAME = '$err$';
const JAVASCRIPT_CODE_ADDITIONAL = 
// Listen to incoming messages from the main thread.
'self.onmessage = function(evt) {' +
    'onMessage(evt.data)' +
    '}' + ';' +
    // Reports an error to the main thread.
    'function onErr(err_, msg) {' +
    'var err = err_ instanceof Error ? err_ : new Error(msg)' + ';' +
    'postMessage({' +
    ERROR_MESSAGE_PROPERTY_NAME + ':' + '[' +
    // `message` is a "non-enumerable" property of an `Error` class
    // and it won't be present in `Object.keys(error)`.
    'err.message' + ',' +
    // `stack` is a "non-enumerable" property of an `Error` class
    // and it won't be present in `Object.keys(error)`.
    'err.stack' +
    '].concat(' +
    // Add any "rest" properties of the error such as `name` or `code`.
    'Object.keys(err).map(' +
    'function(k){' +
    'return[k,err[k]]' +
    '}' +
    ')' +
    ')' +
    '})' +
    '}' + ';' +
    // Define a `postMessage()` function that will be used to send messages to the main thread.
    'var postMessage = self.postMessage' + ';' +
    // Listen to any "synchronous" errors that occur inside the worker.
    //
    // "The error event is fired on a Window object when a resource failed to load
    //  or couldn't be used — for example if a script has an execution error."
    //
    // "This event is only generated for script errors thrown synchronously,
    //  such as during initial loading or within event handlers.
    //  If a promise was rejected (including an uncaught throw within an async function)
    //  and no rejection handlers were attached, an `unhandledrejection` event is fired instead."
    //
    'addEventListener("error",' +
    // `evt` argument is an instance of `ErrorEvent` class, which inherits from `Event` class.
    // https://developer.mozilla.org/en-US/docs/Web/API/ErrorEvent
    'function(evt) {' +
    // The worker will halt execution (unless `event.preventDefault()` is called).
    // The error will be propagated to the main thread's console.
    // Notify the main thread about this error so that it could call the `callback`.
    'onErr(evt.error, evt.message)' +
    '}' +
    ')' + ';' +
    // Listen to any "asynchronous" errors that occur inside the worker.
    //
    // "The `unhandledrejection` event is sent to the global scope of a script
    //  when a JavaScript Promise that has no rejection handler is rejected;
    //  typically, this is the window, but may also be a Worker."
    //
    'addEventListener("unhandledrejection",' +
    // `evt` argument is an instance of `PromiseRejectionEvent` class, which inherits from `Event` class.
    // https://developer.mozilla.org/en-US/docs/Web/API/PromiseRejectionEvent
    'function(evt) {' +
    // Prevent the default logging behavior in the browser console.
    // https://developer.mozilla.org/en-US/docs/Web/API/Window/unhandledrejection_event#preventing_default_handling
    'evt.preventDefault()' + ';' +
    // The worker will halt execution (unless `event.preventDefault()` is called).
    // The error will be propagated to the main thread's console.
    // Notify the main thread about this error so that it could call the `callback`.
    'onErr(evt.reason, String(evt.reason))' +
    '}' +
    ')';
